import { HttpParams } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { catchError, map, of, startWith } from 'rxjs';
import { formatMonthPeriodLabel } from 'src/app/components/main/shared/month-navigation/month-navigation.component';
import {
  ProductionEfficiencyCellColor,
  ProductionEfficiencyDetailItem,
  ProductionEfficiencyDetailResponse,
} from 'src/app/models/production-efficiency-matrix.model';
import { ApiService } from 'src/app/services/api.service';

export type ProductionEfficiencyDetailDialogData = {
  from_month: string;
  to_month: string;
  user_id: number;
  company_product_id: number;
};

const COLOR_CSS: Record<ProductionEfficiencyCellColor, string> = {
  green: 'var(--green)',
  orange: 'var(--yellow)',
  red: 'var(--red)',
};

@Component({
  selector: 'app-production-efficiency-detail-dialog',
  templateUrl: './production-efficiency-detail-dialog.component.html',
  styleUrl: './production-efficiency-detail-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDialogModule, MatButtonModule, MatProgressSpinnerModule],
})
export class ProductionEfficiencyDetailDialogComponent {
  private readonly dialog_ref = inject(MatDialogRef<ProductionEfficiencyDetailDialogComponent>);
  private readonly data = inject<ProductionEfficiencyDetailDialogData>(MAT_DIALOG_DATA);
  private readonly api = inject(ApiService);

  private readonly detail_request = toSignal(
    this.api
      .getProductionEfficiencyDetail(
        new HttpParams()
          .set('from_month', this.data.from_month)
          .set('to_month', this.data.to_month)
          .set('user_id', String(this.data.user_id))
          .set('company_product_id', String(this.data.company_product_id))
      )
      .pipe(
        map(detail => ({ status: 'ready' as const, detail, error: null as string | null })),
        catchError(() =>
          of({
            status: 'error' as const,
            detail: null as ProductionEfficiencyDetailResponse | null,
            error: 'No se ha podido cargar el detalle de eficiencia.',
          })
        ),
        startWith({
          status: 'loading' as const,
          detail: null as ProductionEfficiencyDetailResponse | null,
          error: null as string | null,
        })
      ),
    {
      initialValue: {
        status: 'loading' as const,
        detail: null as ProductionEfficiencyDetailResponse | null,
        error: null as string | null,
      },
    }
  );

  readonly is_loading = computed((): boolean => this.detail_request().status === 'loading');
  readonly error_message = computed((): string | null => this.detail_request().error);

  readonly view_model = computed((): ProductionEfficiencyDetailViewModel | null => {
    const detail = this.detail_request().detail;
    if (detail === null) {
      return null;
    }

    return {
      title: `${detail.user.name} · ${detail.company_product.name}`,
      period_label: formatMonthPeriodLabel(detail.from_month, detail.to_month),
      explanation: detail.explanation,
      summary_real_label: detail.summary ? this.formatPercent(detail.summary.real_percent) : null,
      summary_expected_label: detail.summary ? this.formatPercent(detail.summary.expected_percent) : null,
      summary_color_css: detail.summary ? COLOR_CSS[detail.summary.color] : 'var(--grey_text_color)',
      summary_empty_label: detail.summary ? null : 'Sin valoración',
      included: detail.included.map(item => this.serializeItem(item, true)),
      discarded: detail.discarded.map(item => this.serializeItem(item, false)),
    };
  });

  close(): void {
    this.dialog_ref.close();
  }

  private serializeItem(item: ProductionEfficiencyDetailItem, with_contribution: boolean): ProductionEfficiencyDetailItemView {
    return {
      task_label: `${item.product_task_code} · ${item.product_task_title}`,
      category_name: item.category_name,
      budget_label: this.formatHours(item.budget_seconds),
      real_label: this.formatHours(item.real_seconds),
      percent_label: this.formatPercent(item.real_percent),
      contribution_label: with_contribution && item.contribution_percent != null ? this.formatPercent(item.contribution_percent) : null,
      discard_reason_label: item.discard_reason_label ?? null,
    };
  }

  private formatPercent(value: number): string {
    return `${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1)}%`;
  }

  private formatHours(seconds: number): string {
    const hours = seconds / 3600;
    return `${Number.isInteger(hours) ? hours.toFixed(0) : hours.toFixed(1)}h`;
  }
}

interface ProductionEfficiencyDetailItemView {
  task_label: string;
  category_name: string;
  budget_label: string;
  real_label: string;
  percent_label: string;
  contribution_label: string | null;
  discard_reason_label: string | null;
}

interface ProductionEfficiencyDetailViewModel {
  title: string;
  period_label: string;
  explanation: string;
  summary_real_label: string | null;
  summary_expected_label: string | null;
  summary_color_css: string;
  summary_empty_label: string | null;
  included: ProductionEfficiencyDetailItemView[];
  discarded: ProductionEfficiencyDetailItemView[];
}
